import axios from "axios";
import { backendLog } from "../util/logging";
import { logRestaurantClick } from "../util/analytics";
import {
  DESELECT_CUISINE,
  NO_DATA,
  RESTAURANT_DELETE,
  RESTAURANT_HOVER,
  RESTAURANT_SELECT,
  RESTAURANT_UNHOVER,
  RESTAURANT_UNSELECT,
  RESULTS_RECEIVED,
  SELECT_CUISINE,
  UPDATE_CUISINES,
  START_AD_HOC_SEARCH,
  ADD_AD_HOC_RESTAURANT,
  SET_AD_HOC_SEARCH_STATUS,
  PROMPT_FOR_MORE_BATCHES,
  STOP_AD_HOC_SEARCH,
  AD_HOC_SEARCH_COMPLETE,
  CLEAR_AD_HOC_RESULTS,
  RESULTS_UPDATE,
} from "./types";

export const fetchResults =
  ({ lat, lng, radius }, cuisine, keywords, eventId, includeIds, source, listName) =>
  async (dispatch) => {
    if (!lat || !lng) {
      return;
    }
    try {
      const res = await axios.get(
        `/api/restaurants/query?distance=${radius}&lat=${lat}&long=${lng}${
          keywords ? `&keywords=${keywords}` : ""
        }&cuisine=${cuisine || ""}${
          eventId ? `&eventId=${eventId}` : ""
        }${includeIds ? `&include=${includeIds}` : ""}${
          source ? `&source=${source}` : ""
        }${listName ? `&listName=${encodeURIComponent(listName)}` : ""}`
      );
      if (res.data.length === 0 && !cuisine) {
        dispatch({
          type: NO_DATA,
          payload: true,
        });
      }
      dispatch({
        type: RESULTS_RECEIVED,
        payload: res.data,
      });
    } catch (exception) {
      console.log(exception.response.data);
    }
  };

export const getRestaurantById = (id) => async (dispatch) => {
  const options = {
    method: "GET",
    url: "/api/restaurants/" + id,
  };
  try {
    dispatch({
      type: RESULTS_RECEIVED,
      payload: [(await axios(options)).data.restaurant],
    });
  } catch (error) {
    console.log(error.response.data);
  }
};

export const hoverRestaurant = (id) => (dispatch) => {
  dispatch({
    type: RESTAURANT_HOVER,
    payload: id,
  });
};
export const unhoverRestaurant = () => (dispatch) => {
  dispatch({
    type: RESTAURANT_UNHOVER,
  });
};
export const selectRestaurantFromMap = (id, eventContext = null, analyticsSource = null) => (dispatch) => {
  backendLog(`Restaurant selected: ${id}`); // Keep for Elastic
  logRestaurantClick(id, eventContext, analyticsSource); // Add for analytics
  dispatch({
    type: RESTAURANT_SELECT,
    payload: { id, source: "MAP" },
  });
};
export const selectRestaurantFromList = (id, eventContext = null, analyticsSource = null) => (dispatch) => {
  backendLog(`Restaurant selected: ${id}`); // Keep for Elastic
  logRestaurantClick(id, eventContext, analyticsSource); // Add for analytics
  dispatch({
    type: RESTAURANT_SELECT,
    payload: { id, source: "LIST" },
  });
};
export const unselectRestaurant = () => (dispatch) => {
  dispatch({
    type: RESTAURANT_UNSELECT,
  });
};

export const selectCuisine = (cuisineName, cuisineValue) => (dispatch) => {
  dispatch({
    type: SELECT_CUISINE,
    payload: { cuisineName, cuisineValue },
  });
};
export const deselectCuisine = (cuisineName) => (dispatch) => {
  dispatch({
    type: DESELECT_CUISINE,
    payload: { cuisineName },
  });
};

export const updateSelectedCuisines = (selectedCuisines) => (dispatch) => {
  dispatch({
    type: UPDATE_CUISINES,
    payload: selectedCuisines,
  });
};

export const deleteRestaurant = (id) => async (dispatch) => {
  unselectRestaurant();
  console.log("Sending delete request with id " + id);
  await axios.delete(`/api/restaurants/${id}`);
  dispatch({
    type: RESTAURANT_DELETE,
    payload: id,
  });
};

export const resetNoData = () => (dispatch) => {
  dispatch({
    type: NO_DATA,
    payload: false,
  });
};

// Ad Hoc Search Actions
export const startAdHocSearch = (lat, lng, radius) => async (dispatch) => {
  try {
    const token = localStorage.getItem("token");
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": token,
      },
    };

    const res = await axios.post(
      "/api/restaurants/ad-hoc-search",
      { lat, lng, radius },
      config
    );

    if (res.data.alreadyInProgress) {
      dispatch({
        type: SET_AD_HOC_SEARCH_STATUS,
        payload: {
          status: "in_progress",
          jobId: res.data.jobId,
          message: res.data.message,
        },
      });
      return res.data;
    }

    // Start new search
    dispatch({
      type: START_AD_HOC_SEARCH,
      payload: {
        jobId: res.data.jobId,
        location: { lat, lng, radius },
      },
    });

    return res.data;
  } catch (error) {
    console.error("Error starting ad hoc search:", error);
    dispatch({
      type: SET_AD_HOC_SEARCH_STATUS,
      payload: {
        status: "failed",
        error: error.response?.data?.error || "Failed to start discovery search",
      },
    });
    throw error;
  }
};

export const addAdHocRestaurant = (restaurant) => (dispatch) => {
  dispatch({
    type: ADD_AD_HOC_RESTAURANT,
    payload: restaurant,
  });
};

export const setAdHocSearchStatus = (status, jobId, additionalData = {}) => (
  dispatch
) => {
  dispatch({
    type: SET_AD_HOC_SEARCH_STATUS,
    payload: {
      status,
      jobId,
      ...additionalData,
    },
  });
};

export const promptForMoreBatches = () => (dispatch) => {
  dispatch({
    type: PROMPT_FOR_MORE_BATCHES,
  });
};

export const stopAdHocSearch = (jobId) => async (dispatch) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("[stopAdHocSearch] No token found in localStorage");
      throw new Error("Not authenticated");
    }

    if (!jobId) {
      // If no jobId provided, just update local state
      console.log("[stopAdHocSearch] No jobId provided, updating local state only");
      dispatch({
        type: STOP_AD_HOC_SEARCH,
      });
      return;
    }

    console.log(`[stopAdHocSearch] Attempting to cancel job ${jobId} with token: ${token.substring(0, 20)}...`);

    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": token,
      },
    };

    const res = await axios.post(
      `/api/restaurants/ad-hoc-search/${jobId}/cancel`,
      {},
      config
    );

    console.log(`[stopAdHocSearch] Successfully cancelled job ${jobId}`);

    // Update local state
    dispatch({
      type: STOP_AD_HOC_SEARCH,
    });

    dispatch({
      type: SET_AD_HOC_SEARCH_STATUS,
      payload: {
        status: "cancelled",
        jobId: null,
      },
    });

    return res.data;
  } catch (error) {
    console.error("[stopAdHocSearch] Error cancelling ad hoc search:", error);
    console.error("[stopAdHocSearch] Error response:", error.response?.data);
    console.error("[stopAdHocSearch] Error status:", error.response?.status);
    // Still update local state even if API call fails
    dispatch({
      type: STOP_AD_HOC_SEARCH,
    });
    throw error;
  }
};

export const adHocSearchComplete = (data) => (dispatch) => {
  dispatch({
    type: AD_HOC_SEARCH_COMPLETE,
    payload: data,
  });
};

export const clearAdHocResults = () => async (dispatch, getState) => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      // Not authenticated, just clear from UI
      const state = getState();
      const nonAdHocRestaurants = state.restaurant.restaurants.filter(
        (r) => !r.metadata?.adHocSearch
      );
      dispatch({
        type: RESULTS_UPDATE,
        payload: nonAdHocRestaurants,
      });
      dispatch({
        type: CLEAR_AD_HOC_RESULTS,
      });
      return;
    }

    // Delete from backend
    const config = {
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": token,
      },
    };

    await axios.delete("/api/restaurants/ad-hoc-search/clear", config);

    // Clear from UI
    const state = getState();
    const nonAdHocRestaurants = state.restaurant.restaurants.filter(
      (r) => !r.metadata?.adHocSearch
    );

    dispatch({
      type: RESULTS_UPDATE,
      payload: nonAdHocRestaurants,
    });

    dispatch({
      type: CLEAR_AD_HOC_RESULTS,
    });
  } catch (error) {
    console.error("Error clearing ad hoc results:", error);
    // Still clear from UI even if backend call fails
    const state = getState();
    const nonAdHocRestaurants = state.restaurant.restaurants.filter(
      (r) => !r.metadata?.adHocSearch
    );
    dispatch({
      type: RESULTS_UPDATE,
      payload: nonAdHocRestaurants,
    });
    dispatch({
      type: CLEAR_AD_HOC_RESULTS,
    });
  }
};
